import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSuperChatContentStore } from "@/store/useSuperChatContentStore";
import { UpdateChatContentDto } from "@/dto/ChatContentDto";
import Router from "@/constants/router";
import { useModal } from "mui-modal-provider";
import { Confirm } from "@/organism/Confirm";
import { enqueueSnackbar } from "notistack";

/**
 * QNA 컨텐츠 상세 페이지 커스텀 훅
 *
 * @description QNA 컨텐츠 상세 정보 조회 및 관리 기능을 제공
 * @param contentId 조회할 컨텐츠 ID
 * @returns QNA 컨텐츠 상세 페이지 커스텀 훅
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-16
 */
function useDetail(contentId: number) {
  const navigate = useNavigate();
  const { showModal } = useModal();

  const {
    currentContent,
    loading,
    error,
    fetchContentDetail,
    updateContent,
    deleteContent,
  } = useSuperChatContentStore();

  const [isInitialized, setIsInitialized] = useState(false);

  /**
   * 컨텐츠 상세 정보 로드
   */
  const loadContent = useCallback(async () => {
    if (contentId && !isInitialized) {
      try {
        await fetchContentDetail(contentId);
        setIsInitialized(true);
      } catch (error) {
        console.error("컨텐츠 상세 정보 로드 실패:", error);
      }
    }
  }, [contentId, isInitialized, fetchContentDetail]);

  /**
   * 컨텐츠 수정
   */
  const handleContentUpdate = useCallback(
    async (updateData: UpdateChatContentDto) => {
      const modal = showModal(Confirm, {
        title: "컨텐츠 수정",
        content: "컨텐츠 정보를 수정하시겠습니까?",
        okTitle: "수정",
        cancelTitle: "취소",
        onOk: async () => {
          modal.destroy();
          try {
            console.log("컨텐츠 수정 시작:", updateData);
            await updateContent(contentId, updateData);
            console.log("컨텐츠 수정 성공");
            enqueueSnackbar("컨텐츠가 성공적으로 수정되었습니다.", {
              variant: "success",
            });
          } catch (error) {
            console.error("컨텐츠 수정 실패:", error);
            enqueueSnackbar("컨텐츠 수정에 실패했습니다.", {
              variant: "error",
            });
          }
        },
        onCancel: () => {
          modal.destroy();
        },
      });
    },
    [contentId, updateContent, showModal]
  );

  /**
   * 컨텐츠 삭제
   */
  const handleDelete = useCallback(
    async (id: number) => {
      const modal = showModal(Confirm, {
        title: "컨텐츠 삭제",
        content: "정말로 이 컨텐츠를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.",
        okTitle: "삭제",
        cancelTitle: "취소",
        onOk: async () => {
          modal.destroy();
          try {
            await deleteContent(id);
            enqueueSnackbar("컨텐츠가 성공적으로 삭제되었습니다.", {
              variant: "success",
            });
            // 삭제 성공 시 목록 페이지로 이동
            navigate(Router.SuperChatContent);
          } catch (error) {
            console.error("컨텐츠 삭제 실패:", error);
            enqueueSnackbar("컨텐츠 삭제에 실패했습니다.", {
              variant: "error",
            });
          }
        },
        onCancel: () => {
          modal.destroy();
        },
      });
    },
    [deleteContent, navigate, showModal]
  );

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadContent();
  }, [loadContent]);

  return {
    content: currentContent,
    isLoading: loading,
    error,
    handleContentUpdate,
    handleDelete,
  };
}

export default useDetail;
